import * as React from "react";
import {
  Calculator,
  Plus,
  X,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Trash2,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { Card, CardHeader, CardContent, CardTitle } from "../card";
import { Button } from "../button";
import { Input } from "../input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import { Badge } from "../badge";
import { CurrencyValue, PercentValue } from "../value-display";
import { Heading, Text, Label } from "../typography";
import { usePortfolio } from "../../../hooks/usePortfolio";

/**
 * WhatIfSimulator - Scenario simulation page for Analytics
 *
 * Allows users to explore hypothetical price changes for their holdings
 * and see how those changes would affect portfolio value.
 *
 * Features:
 * - Select ticker from existing holdings
 * - Enter custom price for simulation
 * - Multiple active scenarios
 * - Visual comparison of actual vs simulated values
 * - Portfolio-level impact summary
 */

/**
 * Recalculate holding values with a simulated price
 */
function recalculateHolding(holding, simulatedPrice) {
  const units = parseFloat(holding.units) || 0;
  const purchasePrice = parseFloat(holding.purchasePrice) || 0;
  const originalPrice = holding.currentPrice || 0;
  const exchangeRate = holding.exchangeRate || 1;

  // New calculations
  const newTotalValue = units * simulatedPrice;
  const newTotalValueEUR = newTotalValue * exchangeRate;
  const newGain = (simulatedPrice - purchasePrice) * units;
  const newGainEUR = newGain * exchangeRate;
  const newGainPercent =
    purchasePrice > 0
      ? ((simulatedPrice - purchasePrice) / purchasePrice) * 100
      : 0;

  // Difference from current
  const valueDifference = newTotalValue - (holding.totalValue || 0);
  const valueDifferenceEUR = newTotalValueEUR - (holding.totalValueEUR || 0);
  const priceDifference = simulatedPrice - originalPrice;
  const priceChangePercent =
    originalPrice > 0
      ? ((simulatedPrice - originalPrice) / originalPrice) * 100
      : 0;

  return {
    ...holding,
    originalPrice,
    currentPrice: simulatedPrice,
    totalValue: newTotalValue,
    totalValueEUR: newTotalValueEUR,
    gain: newGain,
    gainEUR: newGainEUR,
    gainPercent: newGainPercent,
    valueDifference,
    valueDifferenceEUR,
    priceDifference,
    priceChangePercent,
    isSimulated: true,
  };
}

/**
 * ScenarioForm - Form for adding new what-if scenarios
 */
function ScenarioForm({ tickers, whatIfPrices, onAddScenario }) {
  const [selectedTicker, setSelectedTicker] = React.useState("");
  const [priceInput, setPriceInput] = React.useState("");

  // Filter out tickers that already have scenarios
  const availableTickers = React.useMemo(() => {
    return tickers.filter((t) => !whatIfPrices[t.ticker]);
  }, [tickers, whatIfPrices]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const price = parseFloat(priceInput);
    if (selectedTicker && !isNaN(price) && price > 0) {
      onAddScenario(selectedTicker, price);
      setSelectedTicker("");
      setPriceInput("");
    }
  };

  const selectedTickerData = tickers.find((t) => t.ticker === selectedTicker);

  return (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-primary" />
          <CardTitle className="text-sm font-medium">Add Simulation</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Ticker Selection */}
            <div className="space-y-1.5">
              <Label className="text-xs">Select Holding</Label>
              <Select value={selectedTicker} onValueChange={setSelectedTicker}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a ticker..." />
                </SelectTrigger>
                <SelectContent>
                  {availableTickers.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      No tickers available
                    </SelectItem>
                  ) : (
                    availableTickers.map((t) => (
                      <SelectItem key={t.ticker} value={t.ticker}>
                        <span className="font-medium">{t.ticker}</span>
                        <span className="text-muted-foreground ml-2">
                          ({t.name})
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Price Input */}
            <div className="space-y-1.5">
              <Label className="text-xs">
                Simulated Price
                {selectedTickerData && (
                  <span className="text-muted-foreground ml-1">
                    ({selectedTickerData.currency})
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={
                    selectedTickerData
                      ? `Current: ${selectedTickerData.currentPrice?.toFixed(2)}`
                      : "Enter price..."
                  }
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  className="pr-12"
                />
                {selectedTickerData && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    {selectedTickerData.currency}
                  </span>
                )}
              </div>
            </div>

            {/* Add Button */}
            <div className="space-y-1.5 flex items-end">
              <Button
                type="submit"
                className="w-full sm:w-auto"
                disabled={
                  !selectedTicker ||
                  !priceInput ||
                  parseFloat(priceInput) <= 0
                }
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Scenario
              </Button>
            </div>
          </div>

          {/* Helper text */}
          {selectedTickerData && (
            <Text variant="secondary" size="xs">
              Current price:{" "}
              <CurrencyValue
                value={selectedTickerData.currentPrice}
                currency={selectedTickerData.currency}
                size="xs"
              />
              {" | "}
              You own {selectedTickerData.units} units
            </Text>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * ActiveScenarios - Display and manage active what-if scenarios
 */
function ActiveScenarios({ scenarios, tickers, onRemove, onClearAll }) {
  if (scenarios.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">
              Active Scenarios ({scenarios.length})
            </CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onClearAll}>
            <Trash2 className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="flex flex-wrap gap-2">
          {scenarios.map(({ ticker, price }) => {
            const tickerData = tickers.find((t) => t.ticker === ticker);
            const originalPrice = tickerData?.currentPrice || 0;
            const priceChange =
              originalPrice > 0
                ? ((price - originalPrice) / originalPrice) * 100
                : 0;

            return (
              <Badge
                key={ticker}
                variant={priceChange >= 0 ? "success" : "destructive"}
                size="lg"
                className="gap-2 pr-1"
              >
                <span className="font-semibold">{ticker}</span>
                <span className="font-mono">
                  {tickerData?.currency === "EUR" ? "\u20AC" : "$"}
                  {price.toFixed(2)}
                </span>
                <span className="text-xs opacity-80">
                  (was {originalPrice.toFixed(2)})
                </span>
                <PercentValue
                  value={priceChange}
                  size="xs"
                  showSign
                  colorize={false}
                  className="opacity-90"
                />
                <button
                  onClick={() => onRemove(ticker)}
                  className="ml-1 p-0.5 rounded hover:bg-background/20 transition-colors"
                  aria-label={`Remove ${ticker} scenario`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * ImpactSummary - Shows portfolio-level impact of simulations
 */
function ImpactSummary({ actualSummary, simulatedSummary, hasSimulations }) {
  if (!hasSimulations) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-muted p-2">
            <Calculator className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <Text variant="secondary" size="sm">
              Add scenarios above to see how price changes would affect your
              portfolio
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  const portfolioChange =
    simulatedSummary.totalValueEUR - actualSummary.totalValueEUR;
  const portfolioChangePercent =
    actualSummary.totalValueEUR > 0
      ? (portfolioChange / actualSummary.totalValueEUR) * 100
      : 0;
  const gainChange = simulatedSummary.totalGainEUR - actualSummary.totalGainEUR;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Simulated Portfolio Value */}
      <Card className="p-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "rounded-full p-2",
              portfolioChange >= 0 ? "bg-success/10" : "bg-destructive/10"
            )}
          >
            {portfolioChange >= 0 ? (
              <TrendingUp className="h-4 w-4 text-success" />
            ) : (
              <TrendingDown className="h-4 w-4 text-destructive" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <Label className="text-xs">Simulated Portfolio Value</Label>
            <div className="flex items-center gap-2">
              <CurrencyValue
                value={simulatedSummary.totalValueEUR}
                size="lg"
                className="font-bold"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Change from Actual */}
      <Card className="p-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "rounded-full p-2",
              portfolioChange >= 0 ? "bg-success/10" : "bg-destructive/10"
            )}
          >
            <Calculator className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <Label className="text-xs">Change from Actual</Label>
            <div className="flex items-center gap-2 mt-0.5">
              <CurrencyValue
                value={portfolioChange}
                size="lg"
                showSign
                colorize
                className="font-bold"
              />
              <PercentValue
                value={portfolioChangePercent}
                size="sm"
                showTrend
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Simulated Total Gain */}
      <Card className="p-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "rounded-full p-2",
              simulatedSummary.totalGainEUR >= 0
                ? "bg-success/10"
                : "bg-destructive/10"
            )}
          >
            {simulatedSummary.totalGainEUR >= 0 ? (
              <TrendingUp className="h-4 w-4 text-success" />
            ) : (
              <TrendingDown className="h-4 w-4 text-destructive" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <Label className="text-xs">Simulated Total Gain</Label>
            <div className="flex items-center gap-2 mt-0.5">
              <CurrencyValue
                value={simulatedSummary.totalGainEUR}
                size="lg"
                showSign
                colorize
                className="font-bold"
              />
              <Text variant="secondary" size="xs">
                (
                {gainChange >= 0 ? "+" : ""}
                <CurrencyValue
                  value={gainChange}
                  size="xs"
                  colorize
                  showSign={false}
                />
                )
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * SimulatedHoldingsTable - Table showing holdings with simulated values
 */
function SimulatedHoldingsTable({ holdings, loading }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!holdings || holdings.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Text variant="secondary">No holdings to display</Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm font-medium">Holdings Overview</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Units</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">Value (EUR)</TableHead>
              <TableHead className="text-right">Gain/Loss</TableHead>
              <TableHead className="text-right">Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {holdings.map((holding) => (
              <TableRow
                key={holding.holdingId}
                className={cn(
                  holding.isSimulated &&
                    "bg-primary/5 border-l-2 border-l-primary"
                )}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="font-medium flex items-center gap-1.5">
                        {holding.name || holding.assetName}
                        {holding.isSimulated && (
                          <Badge variant="default" size="sm">
                            Simulated
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {holding.ticker}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {parseFloat(holding.units).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-col items-end">
                    <CurrencyValue
                      value={holding.currentPrice}
                      currency={holding.currency}
                      size="sm"
                      className={cn(holding.isSimulated && "font-bold")}
                    />
                    {holding.isSimulated && (
                      <Text variant="secondary" size="xs" className="line-through">
                        <CurrencyValue
                          value={holding.originalPrice}
                          currency={holding.currency}
                          size="xs"
                          className="text-muted-foreground"
                        />
                      </Text>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <CurrencyValue
                    value={holding.totalValueEUR}
                    size="sm"
                    className={cn(holding.isSimulated && "font-bold")}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-col items-end">
                    <CurrencyValue
                      value={holding.gainEUR}
                      size="sm"
                      showSign
                      colorize
                    />
                    <PercentValue
                      value={holding.gainPercent}
                      size="xs"
                      showTrend={false}
                    />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {holding.isSimulated ? (
                    <div className="flex flex-col items-end">
                      <CurrencyValue
                        value={holding.valueDifferenceEUR}
                        size="sm"
                        showSign
                        colorize
                        className="font-semibold"
                      />
                      <PercentValue
                        value={holding.priceChangePercent}
                        size="xs"
                        showTrend
                      />
                    </div>
                  ) : (
                    <Text variant="secondary" size="sm">
                      --
                    </Text>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/**
 * WhatIfSimulator - Main component
 */
export function WhatIfSimulator() {
  const { holdings, loading, refresh } = usePortfolio();
  const [whatIfPrices, setWhatIfPrices] = React.useState({});
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Get unique tickers with their data for the dropdown
  const tickers = React.useMemo(() => {
    if (!holdings) return [];

    // Filter out liabilities and non-tradeable assets
    const tradeable = holdings.filter(
      (h) =>
        !h.isLiability &&
        h.ticker &&
        h.currentPrice > 0 &&
        !["Cash/Bank Account", "Real Estate", "Real Estate & Property"].includes(
          h.assetType
        )
    );

    // Create unique ticker list with aggregated data
    const tickerMap = new Map();
    tradeable.forEach((h) => {
      if (!tickerMap.has(h.ticker)) {
        tickerMap.set(h.ticker, {
          ticker: h.ticker,
          name: h.name || h.assetName,
          currency: h.currency,
          currentPrice: h.currentPrice,
          units: parseFloat(h.units) || 0,
        });
      } else {
        // Aggregate units for same ticker
        const existing = tickerMap.get(h.ticker);
        existing.units += parseFloat(h.units) || 0;
      }
    });

    return Array.from(tickerMap.values()).sort((a, b) =>
      a.ticker.localeCompare(b.ticker)
    );
  }, [holdings]);

  // Calculate simulated holdings
  const simulatedHoldings = React.useMemo(() => {
    if (!holdings) return [];

    return holdings.map((holding) => {
      const simulatedPrice = whatIfPrices[holding.ticker];
      if (simulatedPrice !== undefined && holding.currentPrice > 0) {
        return recalculateHolding(holding, simulatedPrice);
      }
      return { ...holding, isSimulated: false };
    });
  }, [holdings, whatIfPrices]);

  // Calculate actual summary
  const actualSummary = React.useMemo(() => {
    if (!holdings) return { totalValueEUR: 0, totalGainEUR: 0 };

    const assets = holdings.filter((h) => !h.isLiability);
    return {
      totalValueEUR: assets.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      totalGainEUR: assets.reduce((sum, h) => sum + (h.gainEUR || 0), 0),
    };
  }, [holdings]);

  // Calculate simulated summary
  const simulatedSummary = React.useMemo(() => {
    const assets = simulatedHoldings.filter((h) => !h.isLiability);
    return {
      totalValueEUR: assets.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      totalGainEUR: assets.reduce((sum, h) => sum + (h.gainEUR || 0), 0),
    };
  }, [simulatedHoldings]);

  // Get active scenarios for display
  const activeScenarios = React.useMemo(() => {
    return Object.entries(whatIfPrices).map(([ticker, price]) => ({
      ticker,
      price,
    }));
  }, [whatIfPrices]);

  const handleAddScenario = (ticker, price) => {
    setWhatIfPrices((prev) => ({
      ...prev,
      [ticker]: price,
    }));
  };

  const handleRemoveScenario = (ticker) => {
    setWhatIfPrices((prev) => {
      const newPrices = { ...prev };
      delete newPrices[ticker];
      return newPrices;
    });
  };

  const handleClearAll = () => {
    setWhatIfPrices({});
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const hasSimulations = activeScenarios.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Heading level={4} className="mb-0.5">
            What-If Simulation
          </Heading>
          <Text variant="secondary" size="sm">
            Explore how price changes would affect your portfolio
          </Text>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-1" />
          )}
          Refresh
        </Button>
      </div>

      {/* Scenario Form */}
      <ScenarioForm
        tickers={tickers}
        whatIfPrices={whatIfPrices}
        onAddScenario={handleAddScenario}
      />

      {/* Active Scenarios */}
      <ActiveScenarios
        scenarios={activeScenarios}
        tickers={tickers}
        onRemove={handleRemoveScenario}
        onClearAll={handleClearAll}
      />

      {/* Impact Summary */}
      <ImpactSummary
        actualSummary={actualSummary}
        simulatedSummary={simulatedSummary}
        hasSimulations={hasSimulations}
      />

      {/* Simulated Holdings Table */}
      <SimulatedHoldingsTable holdings={simulatedHoldings} loading={loading} />
    </div>
  );
}

export default WhatIfSimulator;
